// VariantTabsWithCitations — the tabbed variant viewer. Each composer
// draft carries 1-3 body variants (plain / warm / concise, or a single
// canonical). The reviewer switches tabs and the body updates instantly
// (no re-fetch — every variant ships in the draft row).
//
// Body text carries inline `[^N]` citation markers. `resolveCitations`
// splits the body into prose + citation segments; each citation marker
// renders as a clickable superscript that scrolls to the footnote table
// at the bottom. Each footnote shows the citation `claim` plus the
// `source_type` / `source_id` of the row it grounds in.

import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import type { BaseDraftVariant, ComposerCitation } from '@/data/types';
import { resolveCitations } from '@/lib/resolve-citations';

interface VariantTabsWithCitationsProps {
    variants: BaseDraftVariant[];
    /**
     * Draft-level citation fallback. Composers attach citations per
     * variant (`variant.citations`); this is used only when a variant
     * carries none of its own.
     */
    citations?: ComposerCitation[];
    /** Index of the variant the agent selected — opens on that tab. */
    selectedVariantIndex?: number;
}

export default function VariantTabsWithCitations({
    variants,
    citations = [],
    selectedVariantIndex = 0,
}: VariantTabsWithCitationsProps) {
    const initial =
        selectedVariantIndex >= 0 && selectedVariantIndex < variants.length
            ? selectedVariantIndex
            : 0;
    const [activeIndex, setActiveIndex] = useState(initial);

    const activeVariant = variants[activeIndex] ?? variants[0];
    // Prefer the active variant's own citations (the ontology stores them
    // on `ComposerVariantSchema.citations`); fall back to the draft-level
    // list for any composer that puts them on the envelope instead.
    const activeCitations = useMemo(
        () =>
            activeVariant?.citations && activeVariant.citations.length > 0
                ? activeVariant.citations
                : citations,
        [activeVariant, citations],
    );
    const resolved = useMemo(
        () => resolveCitations(activeVariant?.body ?? '', activeCitations),
        [activeVariant, activeCitations],
    );

    // Footnote anchor id — scoped to the active variant so superscripts
    // scroll to the right footnote when the reviewer switches tabs.
    const footnoteId = (index: number): string => `workbench-footnote-${activeIndex}-${index}`;

    if (variants.length === 0) {
        return (
            <div className="rounded-md border border-[rgb(var(--color-divider))] p-3 text-annotation text-[rgb(var(--color-text-tertiary))]">
                No variants on this draft.
            </div>
        );
    }

    return (
        <div
            data-testid="workbench-variant-tabs"
            className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)]"
        >
            {/* Tab strip */}
            <div className="flex items-center gap-1 border-b border-[rgb(var(--color-divider))] px-2 py-1.5 flex-wrap">
                {variants.map((v, i) => (
                    <button
                        key={i}
                        type="button"
                        data-testid="workbench-variant-tab"
                        onClick={() => setActiveIndex(i)}
                        className={cn(
                            'px-2.5 py-1 rounded-button text-annotation transition-colors capitalize',
                            i === activeIndex
                                ? 'bg-[rgb(var(--color-accent)/0.15)] text-[rgb(var(--color-accent))]'
                                : 'text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        {v.kind}
                    </button>
                ))}
            </div>

            {/* Body — prose with clickable citation superscripts. */}
            <div
                data-testid="workbench-variant-body"
                className="px-3 py-3 text-annotation text-[rgb(var(--color-text-primary))] leading-relaxed whitespace-pre-wrap"
            >
                {resolved.segments.map((seg, i) => {
                    if (seg.kind === 'text') {
                        return <span key={i}>{seg.text}</span>;
                    }
                    // Citation marker — clickable superscript → footnote anchor.
                    return (
                        <a
                            key={i}
                            data-testid="workbench-citation-marker"
                            href={`#${footnoteId(seg.footnoteIndex)}`}
                            className={cn(
                                'align-super text-[10px] font-mono no-underline',
                                seg.missing
                                    ? 'text-[rgb(var(--color-text-tertiary))]'
                                    : 'text-[rgb(var(--color-accent))] hover:underline',
                            )}
                            title={
                                seg.citation
                                    ? seg.citation.claim
                                    : `Unresolved citation [^${seg.marker}]`
                            }
                        >
                            [{seg.footnoteIndex}]
                        </a>
                    );
                })}
            </div>

            {/* Footnote table */}
            {resolved.footnotes.length > 0 && (
                <div
                    data-testid="workbench-footnotes"
                    className="border-t border-[rgb(var(--color-divider))] px-3 py-2.5 flex flex-col gap-1"
                >
                    <div className="text-card-title text-[rgb(var(--color-text-primary))]">
                        Citations
                    </div>
                    <ol className="flex flex-col gap-1">
                        {resolved.footnotes.map((fn) => (
                            <li
                                key={fn.index}
                                id={footnoteId(fn.index)}
                                data-testid="workbench-footnote"
                                className="text-annotation scroll-mt-4"
                            >
                                <span className="font-mono text-[rgb(var(--color-accent))]">
                                    [{fn.index}]
                                </span>{' '}
                                {fn.citation ? (
                                    <>
                                        <span className="text-[rgb(var(--color-text-primary))]">
                                            {fn.citation.claim}
                                        </span>
                                        <span className="ml-1.5 font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                                            {fn.citation.sourceType}
                                            {fn.citation.sourceId
                                                ? ` · ${fn.citation.sourceId}`
                                                : ''}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-[rgb(var(--color-text-tertiary))] italic">
                                        Unresolved citation marker [^{fn.marker}] — no source on
                                        file
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
